import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Search, Plus, Edit, Trash2, Phone, Mail, User, Wrench, Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { getClients, createClient, updateClient, deleteClient } from "../lib/api";
import { toast } from "sonner";
import Fuse from "fuse.js";

const emptyClient = {
  nom: "",
  prenom: "",
  societe: "",
  telephone: "",
  telephone2: "",
  email: "",
  adresse: ""
};

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState(emptyClient);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des clients");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback((searchTerm) => {
    setSearch(searchTerm);
    
    if (!searchTerm.trim()) {
      setFilteredClients(clients);
      return;
    }

    const fuse = new Fuse(clients, {
      keys: ['nom', 'prenom', 'societe', 'telephone', 'telephone2', 'email'],
      threshold: 0.4,
      includeScore: true
    });

    const results = fuse.search(searchTerm);
    setFilteredClients(results.map(r => r.item));
  }, [clients]);

  const handleOpenDialog = (client = null) => {
    if (client) {
      setSelectedClient(client);
      setFormData({
        nom: client.nom,
        prenom: client.prenom,
        societe: client.societe || "",
        telephone: client.telephone,
        telephone2: client.telephone2 || "",
        email: client.email || "",
        adresse: client.adresse || ""
      });
    } else {
      setSelectedClient(null);
      setFormData(emptyClient);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedClient(null);
    setFormData(emptyClient);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nom.trim() || !formData.prenom.trim() || !formData.telephone.trim() || !formData.email.trim()) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        telephone2: formData.telephone2 || null,
        email: formData.email || null
      };
      
      if (selectedClient) {
        await updateClient(selectedClient.id, dataToSend);
        toast.success("Client modifié avec succès");
      } else {
        await createClient(dataToSend);
        toast.success("Client créé avec succès");
      }
      handleCloseDialog();
      loadClients();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedClient) return;
    
    try {
      await deleteClient(selectedClient.id);
      toast.success("Client supprimé");
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      loadClients();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
      console.error(error);
    }
  };

  const openDeleteDialog = (client) => {
    setSelectedClient(client);
    setDeleteDialogOpen(true);
  };

  const handleNewReparation = (clientId) => {
    navigate(`/reparations?newFor=${clientId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Clients
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {clients.length} client{clients.length > 1 ? 's' : ''} enregistré{clients.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button 
          className="bg-[#84CC16] hover:bg-[#65A30D] text-white gap-2"
          onClick={() => handleOpenDialog()}
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4" />
          Nouveau client
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher par nom, téléphone..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
              data-testid="client-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Clients List */}
      <Card data-testid="clients-list">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-lg">Liste des clients</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredClients.length === 0 ? (
            <div className="text-center py-8">
              <User className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="font-medium text-slate-700">
                {search ? "Aucun résultat" : "Aucun client"}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {search 
                  ? "Essayez avec un autre terme de recherche"
                  : "Commencez par ajouter votre premier client"
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Société</th>
                    <th>Téléphone</th>
                    <th>Email</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id} data-testid={`client-row-${client.id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {client.prenom} {client.nom}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-slate-600">{client.societe || "-"}</span>
                      </td>
                      <td>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-slate-600">
                            <Phone className="w-3 h-3" />
                            <span>{client.telephone}</span>
                          </div>
                          {client.telephone2 && (
                            <div className="flex items-center gap-1 text-slate-500 text-sm">
                              <Phone className="w-3 h-3" />
                              <span>{client.telephone2}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {client.email ? (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[150px]">{client.email}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[#84CC16] border-[#84CC16]/30 hover:bg-[#84CC16]/10"
                            onClick={() => handleNewReparation(client.id)}
                            title="Nouvelle réparation"
                          >
                            <Wrench className="w-4 h-4" />
                          </Button>
                          <Link to={`/clients/${client.id}`}>
                            <Button variant="ghost" size="sm" title="Voir détails">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(client)}
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {selectedClient ? "Modifier le client" : "Nouveau client"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="societe">Société (optionnel)</Label>
                <Input
                  id="societe"
                  value={formData.societe || ""}
                  onChange={(e) => setFormData({...formData, societe: e.target.value})}
                  placeholder="Nom de la société"
                />
              </div>
              <div>
                <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    placeholder="Nom"
                    required
                    data-testid="client-nom-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    placeholder="Prénom"
                    required
                    data-testid="client-prenom-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone">Téléphone *</Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    placeholder="06 12 34 56 78"
                    required
                    data-testid="client-telephone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telephone2">Téléphone 2</Label>
                  <Input
                    id="telephone2"
                    type="tel"
                    value={formData.telephone2}
                    onChange={(e) => setFormData({...formData, telephone2: e.target.value})}
                    placeholder="Optionnel"
                    data-testid="client-telephone2-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@exemple.com"
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adresse">Adresse</Label>
                <Input
                  id="adresse"
                  value={formData.adresse}
                  onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  placeholder="Adresse complète"
                  data-testid="client-adresse-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-[#84CC16] hover:bg-[#65A30D] text-white"
                disabled={saving}
                data-testid="client-save-btn"
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le client ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le client "{selectedClient?.prenom} {selectedClient?.nom}" 
              sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
